import React, { useState } from 'react';

import { API_BASE_URL } from '../config';

interface EvalError {
  type: string;
  description: string;
  relatedWord?: string;
  relatedGrammar?: string;
}

interface EvalResult {
  isCorrect: boolean;
  overallScore: number;
  errors: EvalError[];
  feedback: string;
  vocabularyUpdated: boolean;
  wordsUpdatedCount: number;
}

export default function TranslationEvalTestPage() {
  const [originalSentence, setOriginalSentence] = useState('');
  const [correctTranslation, setCorrectTranslation] = useState('');
  const [userTranslation, setUserTranslation] = useState('');
  const [locale, setLocale] = useState('zh-TW');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EvalResult | null>(null);
  const [rawResponse, setRawResponse] = useState('');
  const [error, setError] = useState('');

  const handleEvaluate = async () => {
    if (!originalSentence.trim() || !correctTranslation.trim() || !userTranslation.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setResult(null);
    setRawResponse('');
    setError('');

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/v1/learning/comprehension/evaluate-translation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          courseId: 'test-eval',
          sentenceIndex: 0,
          originalSentence,
          correctTranslation,
          userTranslation,
          locale,
        }),
      });

      const data = await res.json();
      setRawResponse(JSON.stringify(data, null, 2));

      if (res.ok) {
        setResult(data);
      } else {
        setError(`API Error ${res.status}: ${data.message || JSON.stringify(data)}`);
      }
    } catch (err: any) {
      setError(`Network Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '40px auto', padding: '0 20px', fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ marginBottom: 8 }}>Translation Evaluation Test</h1>
      <p style={{ color: '#666', marginBottom: 24 }}>Test the AI translation evaluation prompt with custom inputs.</p>

      {/* Inputs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Original English Sentence</label>
          <textarea
            value={originalSentence}
            onChange={(e) => setOriginalSentence(e.target.value)}
            placeholder="e.g. Children grow fast, so buying used clothes and toys saves money."
            rows={2}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Reference Translation</label>
          <textarea
            value={correctTranslation}
            onChange={(e) => setCorrectTranslation(e.target.value)}
            placeholder="e.g. 孩子們長得很快，所以購買二手衣服和玩具可以省錢。"
            rows={2}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Student Translation</label>
          <textarea
            value={userTranslation}
            onChange={(e) => setUserTranslation(e.target.value)}
            placeholder="e.g. 小孩長很快，所以買使用過的衣服跟玩具更便宜。"
            rows={2}
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontWeight: 600 }}>Locale:</label>
          <select value={locale} onChange={(e) => setLocale(e.target.value)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc' }}>
            <option value="zh-TW">zh-TW</option>
            <option value="zh-CN">zh-CN</option>
            <option value="en">en</option>
            <option value="ko">ko</option>
          </select>

          <button
            onClick={handleEvaluate}
            disabled={loading}
            style={{
              marginLeft: 'auto',
              padding: '10px 28px',
              background: loading ? '#999' : '#4f46e5',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 16,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Evaluating...' : 'Evaluate'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginTop: 20, padding: 16, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626' }}>
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{ marginTop: 24 }}>
          <h2>Result</h2>

          {/* Score */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 4,
            padding: '16px 32px',
            background: result.overallScore >= 90 ? '#dcfce7' : result.overallScore >= 60 ? '#fef9c3' : '#fee2e2',
            borderRadius: 12,
            marginBottom: 16,
          }}>
            <span style={{ fontSize: 48, fontWeight: 700 }}>{result.overallScore}</span>
            <span style={{ fontSize: 18, color: '#666' }}>pts</span>
          </div>

          {/* Feedback */}
          <p style={{ fontSize: 16, color: '#555', marginBottom: 16 }}>{result.feedback}</p>

          {/* Errors */}
          {result.errors.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 8 }}>Errors / Notes:</h3>
              {result.errors.map((err, idx) => (
                <div key={idx} style={{
                  padding: 12,
                  marginBottom: 8,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderLeft: `4px solid ${err.type === 'vocabulary' ? '#f59e0b' : err.type === 'grammar' ? '#3b82f6' : '#8b5cf6'}`,
                  borderRadius: 8,
                }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>{err.type}</div>
                  <div style={{ lineHeight: 1.6 }}>{err.description}</div>
                  {err.relatedWord && <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Related word: <code>{err.relatedWord}</code></div>}
                  {err.relatedGrammar && <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Related grammar: <code>{err.relatedGrammar}</code></div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Raw JSON */}
      {rawResponse && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#666' }}>Raw API Response</summary>
          <pre style={{ background: '#f3f4f6', padding: 16, borderRadius: 8, overflow: 'auto', fontSize: 13, marginTop: 8 }}>
            {rawResponse}
          </pre>
        </details>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 15,
  lineHeight: 1.5,
  resize: 'vertical',
  boxSizing: 'border-box',
};
